import type { Task } from "@tasks/tasksClient";
import { useEffect, useMemo, useState } from "react";

/** How long a signed card holds its stamp before it leaves the list: enough to
 * read the word after the 260ms thunk lands, short enough that the list never
 * feels stuck. */
const HOLD_MS = 700;

const reducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** One row of the tasks list. */
export interface TaskRow {
  task: Task;
  /** The signature landed: this card wears its stamp for a beat, then goes. */
  stamped: boolean;
}

/** A card the list is holding on to, with the slot it occupied. */
interface SigningCard {
  task: Task;
  index: number;
  /** Set once the server has confirmed the signature. Absence from the list is
      not enough: a task also leaves it by being declined. */
  signed: boolean;
}

/**
 * The beat between a signature confirming and the card leaving the list.
 *
 * Signing is deliberately non-optimistic (`useTaskSigning`), and the refetch it
 * fires is also what drops the signed task from the list: the confirmation and
 * the removal land in the same tick, so a card has nothing to stamp: it is
 * simply gone. This hook owns the removal instead. `onSigning` records the card
 * when the button is pressed and holds its slot while the signature is in
 * flight, and `onSignSettled` says what the server ruled.
 *
 * Only a confirmed signature stamps (a 409 counts, someone else's took). The
 * stamp is never inferred from the task leaving the list, because Decline
 * removes a task the same way a signature does: after a refused signature, a
 * decline on the same card would otherwise stamp it "Signed".
 *
 * A refused signature (403, network) drops the record, so the card falls back
 * into the open list and stays actionable for a retry.
 *
 * Under reduced motion there is no beat: the card goes as soon as the data says
 * it is gone.
 */
export const useSignCeremony = (
  tasks: Task[],
): {
  rows: TaskRow[];
  onSigning: (task: Task, index: number) => void;
  onSignSettled: (task: Task, signed: boolean) => void;
} => {
  const [signing, setSigning] = useState<SigningCard[]>([]);

  const rows = useMemo<TaskRow[]>(() => {
    const present = new Set(tasks.map((task) => task.id));
    const open = tasks.map((task) => ({ task, stamped: false }));
    for (const card of signing) {
      if (present.has(card.task.id)) continue;
      // Back into the slot it held, so a stamped card doesn't hop to the end of
      // the list for its last beat. An in-flight card is put back unstamped:
      // the refetch that follows a signature lands before the mutation's own
      // callback does, and without this the card would blink out and return.
      open.splice(Math.min(card.index, open.length), 0, {
        task: card.task,
        stamped: card.signed,
      });
    }
    return open;
  }, [tasks, signing]);

  // The held ids as one primitive: the timer then restarts when the held set
  // actually changes, not on every render.
  const held = rows
    .filter((row) => row.stamped)
    .map((row) => row.task.id)
    .join(" ");

  useEffect(() => {
    if (!held) return;
    const ids = new Set(held.split(" "));
    const timer = setTimeout(
      () => setSigning((prev) => prev.filter((card) => !ids.has(card.task.id))),
      reducedMotion() ? 0 : HOLD_MS,
    );
    return () => clearTimeout(timer);
  }, [held]);

  return {
    rows,
    onSigning: (task, index) =>
      setSigning((prev) => [
        ...prev.filter((card) => card.task.id !== task.id),
        { task, index, signed: false },
      ]),
    onSignSettled: (task, signed) =>
      setSigning((prev) =>
        signed
          ? prev.map((card) =>
              card.task.id === task.id ? { ...card, signed: true } : card,
            )
          : prev.filter((card) => card.task.id !== task.id),
      ),
  };
};
